import type { GraphEdge, GraphNode, OwnershipGraphData } from '../../../types'

function edgeKey(e: GraphEdge): string {
  return `${e.from}|${e.to}|${e.kind}`
}

export interface MergeGraphOptions {
  /** Clear truncated on this node after a successful expand-up from it. */
  clearTruncatedOn?: string
  /** Clear hasDownstream after expand-down under the profile root. */
  clearHasDownstream?: boolean
}

/**
 * Merge a graph fragment (expand up/down) into the current graph.
 * Existing nodes keep truncated=true unless explicitly cleared on the expand anchor.
 */
export function mergeGraphData(
  base: OwnershipGraphData,
  fragment: OwnershipGraphData,
  options?: MergeGraphOptions,
): OwnershipGraphData {
  const nodesById = new Map<string, GraphNode>(base.nodes.map(n => [n.id, { ...n }]))

  for (const n of fragment.nodes) {
    const existing = nodesById.get(n.id)
    if (!existing) {
      nodesById.set(n.id, { ...n })
      continue
    }
    if (n.truncated === true) {
      existing.truncated = true
    }
  }

  if (options?.clearTruncatedOn) {
    const anchor = nodesById.get(options.clearTruncatedOn)
    if (anchor) {
      anchor.truncated = false
    }
  }

  const edgesByKey = new Map<string, GraphEdge>(base.edges.map(e => [edgeKey(e), e]))
  for (const e of fragment.edges) {
    edgesByKey.set(edgeKey(e), e)
  }

  return {
    rootCvr: base.rootCvr,
    nodes: [...nodesById.values()],
    edges: [...edgesByKey.values()],
    hasDownstream: options?.clearHasDownstream ? false : base.hasDownstream,
  }
}
